"use client";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { REACT_QUERY_KEY } from "~/core/constants/common";

import useCurrentOrganization from "~/lib/organizations/hooks/use-current-organization";
import { SocketEvents } from "~/lib/socket/constants";
import { useSocketConnection } from "~/lib/socket/hooks/useSocketConnection";

import { getAllOpportunities } from "../apis/client";
import { useOpportunitiesStore } from "../store";
import type { Opportunity } from "../types/common";
import { mapOpportunityAttributeData } from "../utils/map-attribute-data";

export const useFetchStageWiseOpportunities = (stageId: string) => {
  const queryParams = useOpportunitiesStore(state => state.query);
  const stageWisePagination = useOpportunitiesStore(
    state => state.stageWisePagination,
  );
  const withDefaultView = (queryParams.filters?.length || 0) > 0;
  const activePipeline = useOpportunitiesStore(state => state.activePipeline);
  const setNewOpportunityIds = useOpportunitiesStore(
    state => state.setNewOpportunityIds,
  );
  const setStageWiseLoading = useOpportunitiesStore(
    state => state.setStageWiseLoading,
  );

  const stagePagination = stageWisePagination[stageId] || {
    page: 1,
    limit: 10,
  };

  const { data: opportunitiesData, refetch } = useQuery({
    queryKey: [
      REACT_QUERY_KEY.ALL_OPPORTUNITIES_LISTS_BY_STAGE,
      stageId,
      queryParams.filters ? JSON.stringify(queryParams.filters) : null,
      queryParams.sorting ? JSON.stringify(queryParams.sorting) : null,
      queryParams.q,
      activePipeline?.pipeline_id,
      stagePagination.page,
      stagePagination.limit,
    ],
    queryFn: async ({ queryKey }) => {
      try {
        const [
          _,
          stageId,
          filtersString,
          sortingString,
          q,
          activePipelineId,
          page,
          limit,
        ] = queryKey;

        const filters = filtersString
          ? JSON.parse(filtersString as string)
          : undefined;
        const sorting = sortingString
          ? JSON.parse(sortingString as string)
          : undefined;

        setStageWiseLoading(stageId as string, true);

        const response = await getAllOpportunities({
          filters,
          sorting,
          withDefaultView,
          pipeline_id: activePipelineId as string,
          stage: stageId as string,
          page: page as number,
          limit: limit as number,
          q: q as string,
        });

        return response;
      } catch (error) {
        console.error("Error fetching opportunities:", error);
        setStageWiseLoading(stageId as string, false);
        throw error;
      }
    },
    retry: 1,
    enabled: !!activePipeline?.pipeline_id && !!stageId,
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
  });

  const currentOrg = useCurrentOrganization();

  //Socket event
  const event = SocketEvents.opportunity.listUpdated;
  const room = `${event}:room:${currentOrg?.org_id}`;
  useSocketConnection<{ opportunity_id: string[] }>({
    event,
    room,
    messageCallback: data => {
      refetch();
      setNewOpportunityIds(data.data.opportunity_id);
    },
  });

  useEffect(() => {
    if (!opportunitiesData) return;

    useOpportunitiesStore.setState(state => {
      const mappedOpportunities = mapOpportunityAttributeData(
        opportunitiesData?.opportunities,
      );

      const existingStageOpportunities =
        state.opportunitiesByStageId[stageId] || [];

      const isFirstFetch = opportunitiesData?.metadata.page === 1;
      const updatedOpportunitiesByStageId = isFirstFetch
        ? mappedOpportunities
        : [...existingStageOpportunities, ...mappedOpportunities];

      const opportunities = updatedOpportunitiesByStageId.map(
        (opportunityData): Opportunity => {
          const attributes = opportunityData.attributes;
          return {
            ...attributes,
            ...opportunityData,
          };
        },
      );

      const newStageWisePagination = {
        ...state.stageWisePagination,
        [stageId]: {
          page: opportunitiesData?.metadata.page,
          limit: opportunitiesData?.metadata.limit,
          total: opportunitiesData?.metadata.total,
          total_amount: opportunitiesData?.metadata.total_amount,
        },
      };

      return {
        originalOpportunities: opportunities,
        activeOpportunityData:
          opportunities.length > 0 ? opportunities[0] : null,
        pagination: {
          ...state.pagination,
          page: opportunitiesData?.metadata.page || state.pagination.page,
          limit: opportunitiesData?.metadata.limit || state.pagination.limit,
          total: opportunitiesData?.metadata.total || state.pagination.total,
          total_amount:
            opportunitiesData?.metadata.total_amount ||
            state.pagination.total_amount,
        },
        stageWisePagination: newStageWisePagination,
        opportunitiesByStageId: {
          ...state.opportunitiesByStageId,
          [stageId]: opportunities,
        },
        stageWiseLoading: {
          ...state.stageWiseLoading,
          [stageId]: false,
        },
      };
    });
  }, [opportunitiesData, stageId]);
};

export const useInvalidateStageWiseOpportunitiesData = () => {
  const queryClient = useQueryClient();

  return (
    params: {
      stageId?: string;
      refetchAll?: boolean;
    } = { refetchAll: true },
  ) => {
    const queryKey: string[] = [
      REACT_QUERY_KEY.ALL_OPPORTUNITIES_LISTS_BY_STAGE,
    ];
    if (params.stageId) queryKey.push(params.stageId);
    queryClient.invalidateQueries({
      queryKey,
      type: params.refetchAll ? "all" : "active",
      refetchType: params.refetchAll ? "all" : "active",
    });
  };
};
